import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import {
  Bluetooth,
  CheckCircle2,
  Printer,
  RefreshCw,
  Save,
  TestTube2,
  Trash2,
  Usb
} from "lucide-react";
import {
  clearPrinterSettings,
  getPrinterSettings,
  isElectron,
  listPrinters,
  printPrinterTest,
  savePrinterSettings
} from "../../renderer/platform";
import style from "./PrinterSettings.module.scss";

const DEFAULT_SETTINGS = Object.freeze({
  deviceName: "",
  paperWidth: "58mm",
  copies: 1
});

const PAPER_WIDTHS = Object.freeze([
  {
    value: "58mm",
    label: "2 inch",
    description: "58 mm roll · compact receipts"
  },
  {
    value: "80mm",
    label: "3 inch",
    description: "80 mm roll · wider receipts"
  }
]);

const COPY_OPTIONS = Object.freeze([1, 2, 3]);

const normalizeSettings = (settings) => {
  if (!settings || typeof settings !== "object") return { ...DEFAULT_SETTINGS };

  const copies = Number(settings.copies);
  return {
    deviceName: typeof settings.deviceName === "string" ? settings.deviceName : "",
    paperWidth: settings.paperWidth === "80mm" ? "80mm" : "58mm",
    copies: COPY_OPTIONS.includes(copies) ? copies : 1
  };
};

const normalizePrinter = (printer) => {
  if (!printer || typeof printer !== "object") return null;

  const deviceName =
    typeof printer.deviceName === "string" && printer.deviceName.length > 0
      ? printer.deviceName
      : printer.name;
  if (typeof deviceName !== "string" || deviceName.length === 0) return null;

  const displayName =
    typeof printer.displayName === "string" && printer.displayName.length > 0
      ? printer.displayName
      : deviceName;

  return {
    ...printer,
    deviceName,
    displayName
  };
};

const describeError = (error, fallback) =>
  error instanceof Error && error.message ? error.message : fallback;

function PrinterSettings() {
  const desktopAvailable = isElectron();
  const [printers, setPrinters] = useState([]);
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS });
  const [savedSettings, setSavedSettings] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const [notice, setNotice] = useState(null);

  const refreshPrinters = useCallback(async (announceSuccess = false) => {
    setRefreshing(true);
    setNotice(null);

    try {
      const [printerResponse, storedSettings] = await Promise.all([
        listPrinters(),
        getPrinterSettings()
      ]);
      const printerValues = Array.isArray(printerResponse)
        ? printerResponse
        : printerResponse?.printers;
      const normalizedPrinters = (Array.isArray(printerValues) ? printerValues : [])
        .map(normalizePrinter)
        .filter(Boolean);
      const uniquePrinters = Array.from(
        new Map(normalizedPrinters.map((printer) => [printer.deviceName, printer])).values()
      );
      const normalizedStoredSettings = storedSettings
        ? normalizeSettings(storedSettings)
        : null;

      setPrinters(uniquePrinters);
      setSavedSettings(normalizedStoredSettings);
      setSettings(normalizedStoredSettings || { ...DEFAULT_SETTINGS });

      if (announceSuccess) {
        setNotice({
          variant: "success",
          text: `Found ${uniquePrinters.length} installed printer${uniquePrinters.length === 1 ? "" : "s"}.`
        });
      }
    } catch (error) {
      setNotice({
        variant: "danger",
        text: describeError(error, "Installed printers could not be loaded.")
      });
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!desktopAvailable) return;
    void refreshPrinters();
  }, [desktopAvailable, refreshPrinters]);

  const configuredPrinter = useMemo(
    () => printers.find((printer) => printer.deviceName === savedSettings?.deviceName),
    [printers, savedSettings]
  );

  const settingsAreSaved = Boolean(
    savedSettings &&
    settings.deviceName === savedSettings.deviceName &&
    settings.paperWidth === savedSettings.paperWidth &&
    settings.copies === savedSettings.copies
  );
  const selectedPrinterIsAvailable = printers.some(
    (printer) => printer.deviceName === settings.deviceName
  );
  const isBusy = refreshing || activeAction !== null;

  const updateSettings = (field, value) => {
    setSettings((current) => ({ ...current, [field]: value }));
    setNotice(null);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!settings.deviceName) {
      setNotice({ variant: "warning", text: "Select an installed printer before saving." });
      return;
    }

    setActiveAction("save");
    setNotice(null);
    try {
      const request = normalizeSettings(settings);
      const result = await savePrinterSettings(request);
      const persisted = normalizeSettings(result || request);
      setSavedSettings(persisted);
      setSettings(persisted);
      setNotice({ variant: "success", text: "Printer settings saved." });
    } catch (error) {
      setNotice({
        variant: "danger",
        text: describeError(error, "Printer settings could not be saved.")
      });
    } finally {
      setActiveAction(null);
    }
  };

  const handleTestPrint = async () => {
    if (!settingsAreSaved) {
      setNotice({ variant: "warning", text: "Save these settings before printing a test receipt." });
      return;
    }

    setActiveAction("test");
    setNotice(null);
    try {
      const result = await printPrinterTest();
      if (!result?.submitted) {
        throw new Error(result?.message || "The test receipt could not be sent.");
      }
      setNotice({
        variant: "success",
        text: result.printerName
          ? `Test receipt sent to ${result.printerName}.`
          : "Test receipt sent to the configured printer."
      });
    } catch (error) {
      setNotice({
        variant: "danger",
        text: describeError(error, "The test receipt could not be sent.")
      });
    } finally {
      setActiveAction(null);
    }
  };

  const handleForget = async () => {
    if (!window.confirm("Forget the configured printer?")) return;

    setActiveAction("forget");
    setNotice(null);
    try {
      await clearPrinterSettings();
      setSavedSettings(null);
      setSettings({ ...DEFAULT_SETTINGS });
      setNotice({ variant: "success", text: "The configured printer was forgotten." });
    } catch (error) {
      setNotice({
        variant: "danger",
        text: describeError(error, "The configured printer could not be forgotten.")
      });
    } finally {
      setActiveAction(null);
    }
  };

  return (
    <main className={style.page}>
      <div className={style.content}>
        <header className={style.header}>
          <div>
            <div className={style.eyebrow} style={{ marginTop: 20 }}>Desktop hardware</div>
            <h1>Printer settings</h1>
            <p>Choose the thermal printer used for invoices and test it before serving customers.</p>
          </div>
          <Button
            type="button"
            variant="outline-primary"
            onClick={() => void refreshPrinters(true)}
            disabled={!desktopAvailable || isBusy}
          >
            {refreshing ? (
              <Spinner as="span" animation="border" size="sm" aria-hidden="true" />
            ) : (
              <RefreshCw size={17} aria-hidden="true" />
            )}
            <span>Refresh printers</span>
          </Button>
        </header>

        {!desktopAvailable && (
          <Alert variant="info">
            Printer configuration is available in the Orderly desktop application.
          </Alert>
        )}

        <Card className={style.connectionCard}>
          <Card.Body>
            <div className={style.connectionHeader}>
              <div className={style.connectionIcons} aria-hidden="true">
                <Usb size={22} />
                <Bluetooth size={22} />
              </div>
              <div>
                <h2>Connect through your computer</h2>
                <p>
                  USB printers must be installed in Windows or macOS. Bluetooth printers must be
                  paired and installed in the operating system before they appear here.
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>

        {notice && (
          <Alert variant={notice.variant} role="status" aria-live="polite">
            {notice.text}
          </Alert>
        )}

        {savedSettings?.deviceName && !refreshing && !configuredPrinter && (
          <Alert variant="warning">
            The configured printer is currently unavailable. Reconnect it, then refresh this list.
          </Alert>
        )}

        <Form onSubmit={handleSave}>
          <Row className="g-4">
            <Col lg={7}>
              <Card className={style.panel}>
                <Card.Body>
                  <div className={style.sectionHeading}>
                    <div>
                      <h2>Installed printers</h2>
                      <p>Select the exact operating-system print queue to use.</p>
                    </div>
                    {savedSettings?.deviceName && (
                      <Badge bg="success" className={style.savedBadge}>
                        <CheckCircle2 size={14} aria-hidden="true" /> Configured
                      </Badge>
                    )}
                  </div>

                  {refreshing && printers.length === 0 ? (
                    <div className={style.emptyState} role="status">
                      <Spinner animation="border" size="sm" />
                      <span>Looking for installed printers…</span>
                    </div>
                  ) : printers.length === 0 ? (
                    <div className={style.emptyState}>
                      <Printer size={34} aria-hidden="true" />
                      <strong>No installed printers found</strong>
                      <span>Install or pair a printer in the operating system, then refresh.</span>
                    </div>
                  ) : (
                    <div className={style.printerList} role="radiogroup" aria-label="Installed printers">
                      {printers.map((printer, index) => {
                        const selected = settings.deviceName === printer.deviceName;
                        return (
                          <label
                            className={`${style.printerOption} ${selected ? style.selected : ""}`}
                            htmlFor={`installed-printer-${index}`}
                            key={printer.deviceName}
                          >
                            <Form.Check.Input
                              id={`installed-printer-${index}`}
                              type="radio"
                              name="installedPrinter"
                              checked={selected}
                              onChange={() => updateSettings("deviceName", printer.deviceName)}
                              disabled={isBusy}
                            />
                            <span className={style.printerIcon} aria-hidden="true">
                              <Printer size={21} />
                            </span>
                            <span className={style.printerDetails}>
                              <span className={style.printerName}>
                                {printer.displayName}
                                {printer.isDefault && <Badge bg="secondary">OS default</Badge>}
                              </span>
                              {printer.description && (
                                <span className={style.printerDescription}>{printer.description}</span>
                              )}
                              <code>{printer.deviceName}</code>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className={style.panel}>
                <Card.Body>
                  <div className={style.sectionHeading}>
                    <div>
                      <h2>Receipt setup</h2>
                      <p>The invoice layout remains the same and is scaled to the selected roll.</p>
                    </div>
                  </div>

                  <fieldset className={style.paperOptions} disabled={isBusy}>
                    <legend>Paper width</legend>
                    {PAPER_WIDTHS.map((paper) => (
                      <label
                        className={`${style.paperOption} ${settings.paperWidth === paper.value ? style.selected : ""
                          }`}
                        htmlFor={`paper-${paper.value}`}
                        key={paper.value}
                      >
                        <Form.Check.Input
                          id={`paper-${paper.value}`}
                          type="radio"
                          name="paperWidth"
                          checked={settings.paperWidth === paper.value}
                          onChange={() => updateSettings("paperWidth", paper.value)}
                        />
                        <span>
                          <strong>{paper.label}</strong>
                          <small>{paper.description}</small>
                        </span>
                      </label>
                    ))}
                  </fieldset>

                  <Form.Group className={style.copiesControl} controlId="printer-copies">
                    <Form.Label>Copies per invoice</Form.Label>
                    <Form.Select
                      value={settings.copies}
                      onChange={(event) => updateSettings("copies", Number(event.target.value))}
                      disabled={isBusy}
                    >
                      {COPY_OPTIONS.map((copies) => (
                        <option value={copies} key={copies}>
                          {copies}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <div className={style.actions}>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={!desktopAvailable || isBusy || !selectedPrinterIsAvailable}
                    >
                      {activeAction === "save" ? (
                        <Spinner as="span" animation="border" size="sm" aria-hidden="true" />
                      ) : (
                        <Save size={17} aria-hidden="true" />
                      )}
                      <span>Save settings</span>
                    </Button>
                    <Button
                      type="button"
                      variant="outline-primary"
                      onClick={() => void handleTestPrint()}
                      disabled={!desktopAvailable || isBusy || !settingsAreSaved || !configuredPrinter}
                    >
                      {activeAction === "test" ? (
                        <Spinner as="span" animation="border" size="sm" aria-hidden="true" />
                      ) : (
                        <TestTube2 size={17} aria-hidden="true" />
                      )}
                      <span>Test print</span>
                    </Button>
                  </div>

                  {!settingsAreSaved && settings.deviceName && (
                    <p className={style.actionHint}>Save these settings to enable the test print.</p>
                  )}

                  {savedSettings?.deviceName && (
                    <Button
                      type="button"
                      variant="link"
                      className={style.forgetButton}
                      onClick={() => void handleForget()}
                      disabled={isBusy}
                    >
                      {activeAction === "forget" ? (
                        <Spinner as="span" animation="border" size="sm" aria-hidden="true" />
                      ) : (
                        <Trash2 size={16} aria-hidden="true" />
                      )}
                      <span>Forget configured printer</span>
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      </div>
    </main>
  );
}

export default PrinterSettings;
